/**
 * Shared Markdown renderer for all agent output surfaces.
 * Uses react-markdown + remark-gfm with custom code block highlighting.
 */
import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { balanceCodeFences } from '../utils/agentMessageFormatting.js';
import MermaidDiagram from './MermaidDiagram.jsx';
import CodePanel from './CodePanel.jsx';
import { grammarOfFence } from '../utils/codeLang.js';

/**
 * @param {Object} props
 * @param {string} props.content - Markdown text to render
 * @param {'light'|'dark'} [props.theme='light'] - Color theme
 * @param {function} [props.codeHighlighter] - Optional (code, lang) => React node for syntax highlighting
 * @param {string} [props.className]
 */
// ``size``: 'compact' (default — the dense px scale of panels/streams) | 'chat' (a conversation message, Slack-like:
// 15px body / 1.4667 line-height in REM, every element in EM of it — so it scales with the user's root font / zoom).
const SIZES = {
  compact: { base: 'text-[11px]', inline: 'text-[10px]', label: 'text-[9px]', pre: 'text-[10px]', table: 'text-[10px]',
    h1: 'text-[13px]', h2: 'text-[12px]', h3: 'text-[11px]' },
  chat: { base: 'text-[0.9375rem] leading-[1.46668]', inline: 'text-[0.8667em]', label: 'text-[0.75em]',
    pre: 'text-[0.8667em]', table: 'text-[0.8667em]', h1: 'text-[1.2em]', h2: 'text-[1.0667em]', h3: 'text-[1em]' },
};

export default function MarkdownRenderer({ content, theme = 'light', codeHighlighter, className = '', size = 'compact' }) {
  const isDark = theme === 'dark';
  const T = SIZES[size] || SIZES.compact;

  const components = useMemo(() => ({
    // Fenced code blocks (pre > code)
    pre({ children }) {
      // CHAT (a conversation message): every ``` fence — with or without a language — is the Slack-style shaded
      // CODE PANEL (CONTEXT-DOCS §P4): colouring, Copy, long code collapsed. Mermaid stays a diagram.
      if (size === 'chat') {
        const el = React.Children.toArray(children)[0];
        const p = (el && el.props) || {};
        const lang = String(p.className || '').replace('language-', '');
        if (lang !== 'mermaid') {
          return <CodePanel code={String(p.children == null ? '' : p.children).replace(/\n$/, '')}
            grammar={grammarOfFence(lang)} title={lang} testId="md-code-panel" />;
        }
      }
      return <>{children}</>;
    },
    code({ node, inline, className: codeClass, children, ...props }) {
      const lang = (codeClass || '').replace('language-', '');
      const code = String(children).replace(/\n$/, '');
      const isBlock = !inline && lang;

      // PLAN-DIAGRAMS.md: a ```mermaid block renders as a diagram (lazy-loaded), not source. Its own
      // error-catch falls back to the source, so a bad diagram never blanks the surrounding markdown.
      if (isBlock && lang === 'mermaid') {
        return <MermaidDiagram code={code} isDark={isDark} />;
      }

      if (!isBlock) {
        return (
          <code
            className={`px-1 py-0.5 rounded ${T.inline} font-mono ${
              isDark ? 'bg-slate-800 text-emerald-300' : 'bg-slate-100 text-emerald-700'
            }`}
            {...props}
          >
            {code}
          </code>
        );
      }

      return (
        <div className={`my-1.5 rounded overflow-x-auto border ${
          isDark ? 'bg-[#1a1f2e] border-slate-700/50' : 'bg-slate-50 border-slate-200'
        }`}>
          {lang && (
            <div className={`px-2.5 py-0.5 ${T.label} border-b font-mono ${
              isDark ? 'text-slate-400 border-slate-700/40' : 'text-slate-500 border-slate-200'
            }`}>
              {lang}
            </div>
          )}
          <pre className={`px-2.5 py-1.5 ${T.pre} font-mono leading-relaxed whitespace-pre-wrap m-0 ${
            isDark ? 'text-slate-300' : 'text-slate-800'
          }`}>
            <code>
              {codeHighlighter ? codeHighlighter(code, lang) : code}
            </code>
          </pre>
        </div>
      );
    },

    // Tables
    table({ children }) {
      return (
        <div className={`my-1.5 overflow-x-auto rounded border ${
          isDark ? 'border-slate-700/50' : 'border-slate-200'
        }`}>
          <table className={`${T.table} font-mono w-full border-collapse`}>
            {children}
          </table>
        </div>
      );
    },
    thead({ children }) {
      return (
        <thead className={isDark ? 'bg-slate-800/60' : 'bg-slate-100'}>
          {children}
        </thead>
      );
    },
    th({ children }) {
      return (
        <th className={`px-2 py-1 text-left font-semibold border-b ${
          isDark ? 'text-slate-300 border-slate-700/50' : 'text-slate-700 border-slate-200'
        }`}>
          {children}
        </th>
      );
    },
    td({ children }) {
      return (
        <td className={`px-2 py-0.5 border-b ${
          isDark ? 'text-slate-300 border-slate-700/30' : 'text-slate-700 border-slate-100'
        }`}>
          {children}
        </td>
      );
    },
    tr({ children, ...props }) {
      const idx = props.node?.position?.start?.line || 0;
      return (
        <tr className={idx % 2 === 0
          ? (isDark ? 'bg-[#161b22]' : 'bg-white')
          : (isDark ? 'bg-[#1a1f2e]' : 'bg-slate-50/50')
        }>
          {children}
        </tr>
      );
    },

    // Links
    a({ href, children }) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={`hover:underline ${isDark ? 'text-blue-400' : 'text-blue-600'}`}
        >
          {children}
        </a>
      );
    },

    // Headings
    h1({ children }) { return <div className={`${T.h1} font-bold mt-2 mb-1 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>{children}</div>; },
    h2({ children }) { return <div className={`${T.h2} font-bold mt-1.5 mb-0.5 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>{children}</div>; },
    h3({ children }) { return <div className={`${T.h3} font-semibold mt-1 ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{children}</div>; },

    // Paragraphs
    p({ children }) {
      // CHAT (Slack density, MODES.md §3f): the message's own 1.46668 line height, NO paragraph margins — a blank line
      // between paragraphs is a small gap, never a block of air
      return <p className={`${size === 'chat' ? 'm-0 [&:not(:first-child)]:mt-1' : 'my-1 leading-relaxed'} ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{children}</p>;
    },

    // Lists
    ul({ children }) {
      return <ul className={`${size === 'chat' ? 'my-0.5 pl-5' : 'my-1 pl-4 space-y-0.5'} list-disc ${isDark ? 'marker:text-slate-500' : 'marker:text-slate-400'}`}>{children}</ul>;
    },
    ol({ children }) {
      return <ol className={`${size === 'chat' ? 'my-0.5 pl-5' : 'my-1 pl-4 space-y-0.5'} list-decimal ${isDark ? 'marker:text-slate-500' : 'marker:text-slate-400'}`}>{children}</ol>;
    },
    li({ children }) {
      return <li className={`${size === 'chat' ? '' : 'leading-relaxed'} ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{children}</li>;
    },

    // Blockquote
    blockquote({ children }) {
      return (
        <blockquote className={`my-1 pl-3 border-l-2 ${
          isDark ? 'border-slate-600 text-slate-400' : 'border-slate-300 text-slate-600'
        } italic`}>
          {children}
        </blockquote>
      );
    },

    // Bold / Italic
    strong({ children }) {
      return <strong className={`font-semibold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>{children}</strong>;
    },
    em({ children }) {
      return <em className={isDark ? 'text-slate-300' : 'text-slate-600'}>{children}</em>;
    },

    // Horizontal rule
    hr() {
      return <hr className={`my-2 ${isDark ? 'border-slate-700' : 'border-slate-200'}`} />;
    },
  }), [isDark, codeHighlighter, T, size]);

  // An UNCLOSED ```-fence (truncated output / model slip) would otherwise render the whole tail as one
  // raw code block (the ProgramAgent "looks like a .md" case) — balance it so it renders readably.
  const safeContent = useMemo(() => balanceCodeFences(content), [content]);

  if (!content) return null;

  return (
    <div className={`${T.base} ${className}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {safeContent}
      </ReactMarkdown>
    </div>
  );
}
